<template>
    <component :is="as" :class="CSSClasses" class="rich-text-elements">
        <div v-if="html" class="rich-text html" v-html="html" />
        <template v-else v-for="(currentElement, index) in elements?.root?.children" :key="index">
            <div class="block-item" v-if="currentElement.type === 'upload'">
                <ImageBlock v-if="currentElement.relationTo === 'images'" :image="currentElement.value" />
                <VideoBlock
                    v-else-if="currentElement.relationTo === 'videoPosters'"
                    v-bind="{ ...currentElement.value }"
                />
                <Button
                    v-else-if="currentElement.relationTo === 'files'"
                    :url="currentElement.value?.url"
                    variant="tertiary"
                    :newTab="true"
                    class="cta"
                    icon="download"
                    >{{ currentElement.value?.label }}</Button
                >
            </div>
            <div v-else-if="currentElement.type === 'block'" class="block-item" v-html="currentElement.fields?.html" />
            <div v-else v-html="renderedHtml([currentElement])" class="rich-text" />
        </template>
    </component>
</template>

<script setup>
const props = defineProps({
    elements: {
        type: Object,
        default: () => {},
    },
    as: {
        type: [String, Object],
        default: 'div',
    },
    bodyStyle: {
        type: String,
        default: 'serif-large',
        validator: (value) => {
            return [
                'serif-large',
                'serif-small',
                'sans-large',
                'body-sans-large',
                'caption',
                'pullquote',
                'blue',
                'body-display-micro-italic',
                'body-micro-italic',
            ].includes(value);
        },
    },
    color: {
        type: String,
        default: '',
    },
    html: {
        type: String,
        default: '',
    },
    isCentered: {
        type: Boolean,
        default: false,
    },
    footnoteOffset: {
        type: Number,
    },
    size: {
        ...sizes,
    },
    theme: {
        ...themes,
    },
    ...streamBlock,
});

const rtlFlag = computed(() => {
    if (props.elements?.root?.direction && props.elements?.root?.direction === "rtl") {
        return 'direction-rtl'
    }
    else {
        return ''
    }
})

const CSSClasses = computed(() => {
    return [
        `body-style-${props.bodyStyle}`,
        `theme-${props.theme}`,
        `size-${props.size}`,
        props.color && `color-${props.color}`,
        props.pageContext && `page-context-${props.pageContext}`,
        props.isCentered && `centered`,
        rtlFlag.value
    ];
});

const renderedHtml = (element) => {
    return lexicalSerialize(element, props.footnoteOffset);
};
</script>

<style lang="scss" scoped>
.rich-text-elements {
    &.page-context-publication {
        opacity: 0.9;
    }

    &.page-context-collection-block {
        opacity: 0.6;
    }

    &.centered {
        :deep(.rich-text) {
            text-align: center;

            p {
                max-width: none;
            }
        }
    }
    &.theme-dark {
        color: color(blanc, default, 0.9);
    }
    .block-item {
        margin-bottom: vertical-space(2);
    }
    &.body-style-body-display-micro-italic {
        outline: none;
    }
}

.direction-rtl :deep(.rich-text .footnote) {unicode-bidi: plaintext;}

.body-style-body-micro-pub-name {
    display: inline;
}

:deep(.rich-text),
:deep(.block-item) {
    --paragraph-bottom-margin: 1.8rem;
    --list-item-bottom-margin: 1rem;

    .body-style-body-micro-italic & {
        @include type-body-micro-italic;
    }

    .body-style-body-micro-italic.source-label & {
        width: max-content;
        &:before {
            content: ' \B7 ';
            margin-right: 0.2rem;
        }
        @include media-query(mobile) {
            width: 100%;
        }
    }

    .body-style-body-micro-pub-name & {
        display: inline;
        margin-left: 0.3em;
        @include type-body-micro;
    }

    .body-style-caption & {
        --paragraph-bottom-margin: 0.4rem;
    }

    .body-style-body-display-micro-italic & {
        outline: none;
        @include type-body-display-micro-italic;
    }

    h1,
    h2,
    h3 {
        margin-bottom: 1.4rem;
        margin-top: 4rem;

        @include media-query(phone) {
            margin-bottom: 0.8rem;
        }
    }

    &:first-child h1,
    &:first-child h2,
    &:first-child h3 {
        margin-top: 0;
    }

    h4,
    h5 {
        margin-bottom: 0.8rem;
        margin-top: 3rem;
    }

    h1,
    h2,
    h3,
    h4,
    h5 {
        max-width: var(--body-text-max-width);
    }

    p,
    li {
        max-width: var(--body-text-max-width);

        .body-style-serif-large & {
            @include type-body-display-large;
        }

        .body-style-serif-small & {
            @include type-body-display-small;
            &:not(:last-child) {
                margin-bottom: 1.2rem;
            }
        }

        .body-style-sans-large & {
            @include type-body-x-large;
        }

        .body-style-body-sans-large & {
            @include type-body-large;
        }

        .body-style-caption & {
            @include type-body-display-micro;
        }

        .body-style-pullquote & {
            @include type-pullquote;
        }

        .color-blue & {
            color: palette(text-blue);
        }

        &.nested-list-item {
            list-style-type: none;
        }
    }

    p {
        margin-bottom: var(--paragraph-bottom-margin);
    }

    a {
        text-decoration-color: palette(anchor-underline);
        overflow-wrap: break-word;
        @include type-underline;
        &:hover {
            color: color(offnoir, hover);
            text-decoration-color: palette(anchor-underline-hover);
        }
        &:active {
            color: color(offnoir, active);
            text-decoration-color: palette(anchor-underline-active);
        }
    }

    .ol1 {
        list-style-type: decimal;
    }

    .ol2 {
        list-style-type: upper-alpha;
    }

    .ol3 {
        list-style-type: lower-alpha;
    }

    .ol4 {
        list-style-type: upper-roman;
    }

    .ol5 {
        list-style-type: lower-roman;
    }

    ul {
        list-style-type: disc;

        ::marker {
            line-height: 0;
        }
    }

    .ul1 {
        list-style-type: disc;
    }

    .ul2 {
        list-style-type: circle;
    }

    .ul3 {
        list-style-type: square;
    }

    ol,
    ul {
        list-style-position: outside;
        margin-bottom: var(--paragraph-bottom-margin);
        margin-inline-start: 2em;
    }

    li {
        margin-bottom: var(--list-item-bottom-margin);
    }

    sup {
        vertical-align: super;
        padding: 0 0.2rem;
        @include type-label-small;
    }
    .footnote {
        // color: color(teal);
        text-decoration-color: color(teal);
        text-decoration-thickness: 0.1rem;
        text-underline-offset: 0.2rem;
        vertical-align: super;
        padding: 0 0.2rem;
        @include type-label-small;
    }

    .size-100 & * {
        @include margin-lr(auto);
    }

    &:only-child {
        p:last-child {
            margin-bottom: 0;
        }
    }
    blockquote {
        padding-inline-start: 3.2rem;
        border-inline-start: 0.4rem solid var(--bar-color, color(teal));
        margin-bottom: var(--paragraph-bottom-margin);
        color: #004070;
        margin-bottom: 1.5em;
        margin-top: 1.5em;
        @include type-pullquote;
    }
}
//supporting html blocks in rich text
.size-100 .block-item {
    margin-left: auto;
    margin-right: auto;
    max-width: var(--body-text-max-width);
}

// legacy video embeds on event pages
:deep(.videoEmbed) {
    margin-bottom: 4rem;
}
//- avatar
:deep(.avatar) {
    background-position: center center;
    background-repeat: no-repeat;
    background-size: 100% auto;
    border: 1px solid #000000;
    border-radius: 50%;
    display: block;
    float: left;
    height: 6rem;
    width: 6rem;
    margin-right: 2rem !important;
    overflow: hidden;
}
//-table

:deep(table) {
    border-collapse: collapse;
    border-spacing: 0;
    margin-bottom: var(--paragraph-bottom-margin);
    td {
        border-bottom: (rgba(var(--color-rgb-noir-default), 0.1) solid 1px);
        padding: 1.5rem;
    }
    tr.row-background-highlight {
        background-color: rgba(var(--color-rgb-noir-default), 0.1);
    }
    thead td.table-title {
        border-bottom: rgba(var(--color-rgb-tan-default), 1) solid 1px;
        color: rgba(var(--color-rgb-tan-default), 1);
        font-size: 1.3em;
        padding-bottom: 0.5em;
        text-align: left;
        @include media-query(mobile) {
            font-size: 0.8em;
        }
    }
    tbody td,
    tfoot td {
        font-weight: 350;
        font-size: 0.9em;
        padding: 0.7em;
        p {
            font-size: 0.9em !important;
        }
        li {
            font-size: 0.9em !important;
        }
        @include media-query(mobile) {
            font-size: 0.8em;
            padding: 0.5em;
            p {
                font-size: 0.8em !important;
            }
        }
    }
}
//-supporting legacy text box styles
:deep(.text-box) {
    background-color: #e6e7e8;
    display: inline-block;
    margin-top: 0;
    margin-bottom: var(--paragraph-bottom-margin);
    padding: 3rem;
    width: 100%;

    h3,
    h4,
    h5 {
        margin-top: 0;
    }

    h5 {
        color: #000000;
        text-transform: uppercase;
    }

    strong {
        color: #929497;
        font-size: 1.3em;
        padding: 0 3rem 0 0;
    }

    .section {
        margin-top: 0;
    }
}
</style>
